import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Heart, Sparkles, Play, Save, Check, Share2, QrCode, Download, BookOpen, History } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';
import html2canvas from 'html2canvas';
import { trackCompleteTest } from '../utils/metaPixel';
import { mapaArquetiposCapitulos, getColdSeasons, getDeepRecommendations, getArchetypeDisplayName } from '../data/mapData';
import { tensaoQueTeMove, perguntaClinica, defesaCaracteristica, getChapterContent, PALETA_OFICIAL } from '../data/bookContent';
import { TENSAO_RAIZ } from '../data/archetypeContent';
import { questions } from '../data/questionsData';
import { archetypeResults } from '../data/archetypeData';
import { computeResult } from '../utils/scoringEngine';
import MapaEstacoes from './MapaEstacoes';
import DinamicaEstacoes from './DinamicaEstacoes';
import EvolucaoEstacoes from './EvolucaoEstacoes';
import SeloConfiabilidade from './SeloConfiabilidade';
import ResultadoTransicao from './ResultadoTransicao';
import CartaDaEstacao from './CartaDaEstacao';
import JornadaNoTempo from './JornadaNoTempo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TEST_URL = 'https://estacoes.ocoracaodetodasascoisas.com';

// ============ MAPEAMENTO DE INTERPRETAÇÃO PRÁTICA ============
const practicalMapping = {
  alquimista: {
    strengths: [
      "Capacidade de transformar dificuldades em aprendizado",
      "Visão profunda sobre o sentido das experiências",
      "Habilidade para encontrar luz nas situações difíceis",
      "Sabedoria para ressignificar problemas"
    ],
    challenges: [
      "Tendência ao isolamento quando precisa processar emoções",
      "Pode analisar demais ao invés de simplesmente viver",
      "Dificuldade para se abrir emocionalmente com outros",
      "Risco de se perder em reflexões excessivas"
    ],
    developmentAreas: [
      "Cultivar vínculos afetivos mais profundos",
      "Praticar a presença no momento atual",
      "Equilibrar reflexão com ação prática",
      "Desenvolver maior espontaneidade emocional"
    ],
    practicalAdvice: "Você tem um dom natural para transformar experiências em sabedoria, mas precisa se lembrar de que a vida também é para ser vivida, não apenas compreendida. Busque equilibrar seus momentos de reflexão com conexões humanas genuínas."
  },
  amante: {
    strengths: [
      "Capacidade natural de criar vínculos profundos",
      "Empatia e sensibilidade às necessidades dos outros",
      "Habilidade para nutrir relacionamentos com carinho",
      "Presença acolhedora que faz outros se sentirem vistos"
    ],
    challenges: [
      "Tendência a se perder na identidade do outro",
      "Dificuldade para manter limites saudáveis nos relacionamentos",
      "Pode sofrer intensamente com ausências ou rejeições",
      "Risco de negligenciar suas próprias necessidades"
    ],
    developmentAreas: [
      "Fortalecer a autoestima independente de relacionamentos",
      "Desenvolver autonomia emocional",
      "Aprender a amar sem se anular",
      "Cultivar passatempos e interesses individuais"
    ],
    practicalAdvice: "Seu coração generoso é uma força poderosa, mas lembre-se de que amar verdadeiramente inclui amar a si mesmo. Relacionamentos saudáveis florescem quando duas pessoas inteiras se encontram, não quando duas metades se completam."
  },
  peregrino: {
    strengths: [
      "Adaptabilidade natural às mudanças da vida",
      "Coragem para sair da zona de conforto",
      "Visão otimista sobre novos começos",
      "Capacidade de inspirar outros a crescer"
    ],
    challenges: [
      "Dificuldade para se comprometer com projetos de longo prazo",
      "Tendência a fugir quando as coisas ficam difíceis",
      "Pode ter problemas para criar estabilidade",
      "Risco de colecionar experiências sem se aprofundar"
    ],
    developmentAreas: [
      "Desenvolver persistência e constância",
      "Aprender o valor da estabilidade e rotina",
      "Cultivar relacionamentos duradouros",
      "Praticar o enraizamento em projetos importantes"
    ],
    practicalAdvice: "Sua capacidade de movimento e renovação é valiosa, mas a vida também pede momentos de permanência. Tente encontrar formas de crescer e se renovar dentro de compromissos estáveis, ao invés de sempre buscar o novo em outro lugar."
  },
  guardiao: {
    strengths: [
      "Capacidade natural de criar estruturas que funcionam",
      "Confiabilidade e consistência nos compromissos",
      "Habilidade para oferecer segurança aos outros",
      "Disciplina para manter rotinas saudáveis"
    ],
    challenges: [
      "Tendência à rigidez quando as coisas fogem do controle",
      "Dificuldade para se adaptar a mudanças inesperadas",
      "Pode ser excessivamente crítico consigo mesmo e outros",
      "Risco de sufocar a espontaneidade própria e alheia"
    ],
    developmentAreas: [
      "Desenvolver flexibilidade nas expectativas",
      "Aprender a confiar no processo natural das coisas",
      "Cultivar a espontaneidade e o improviso",
      "Praticar a aceitação das imperfeições"
    ],
    practicalAdvice: "Sua capacidade de criar ordem e estabilidade é um dom precioso, mas lembre-se de que a vida também precisa de espaço para fluir. As melhores estruturas são aquelas que sustentam sem oprimir."
  }
};

const dualPracticalMapping = {
  visionario: {
    meaning: "Combinação de transformação pessoal com empatia pelos outros",
    whenItAppears: "Surge quando você está aprendendo a equilibrar sua jornada interior com o cuidado pelas pessoas ao seu redor",
    practicalImpact: [
      "Você consegue ver possibilidades de crescimento tanto para si quanto para outros",
      "Tem facilidade para inspirar pessoas através de sua própria transformação",
      "Pode atuar como um guia natural para quem está passando por mudanças"
    ]
  },
  encontro: {
    meaning: "Capacidade de crescer através de parcerias e colaborações",
    whenItAppears: "Aparece quando sua jornada pessoal está pedindo para ser compartilhada e enriquecida por conexões significativas",
    practicalImpact: [
      "Seus melhores momentos de crescimento acontecem em companhia de outros",
      "Você tem facilidade natural para criar parcerias e colaborações",
      "Tende a encontrar soluções através do diálogo e da troca"
    ]
  },
  guerreiro: {
    meaning: "Combinação de estrutura pessoal com propósito de crescimento",
    whenItAppears: "Emerge quando você está desenvolvendo a disciplina necessária para alcançar objetivos importantes em sua vida",
    practicalImpact: [
      "Você tem determinação para superar obstáculos quando sabe que vale a pena",
      "Consegue manter disciplina em projetos que fazem sentido para você",
      "Equilibra bem a persistência com a flexibilidade para mudanças necessárias"
    ]
  },
  curador: {
    meaning: "Habilidade natural para acolher e ajudar outros sem se perder",
    whenItAppears: "Surge quando você está aprendendo a canalizar sua sensibilidade emocional de forma construtiva e equilibrada",
    practicalImpact: [
      "As pessoas se sentem naturalmente seguras para se abrir com você",
      "Você consegue oferecer apoio emocional sem absorver os problemas dos outros",
      "Tem facilidade para criar ambientes de cura e acolhimento"
    ]
  },
  guardiao_sabio: {
    meaning: "Capacidade de criar estruturas que fazem sentido profundo",
    whenItAppears: "Aparece quando você está unindo sua necessidade de ordem com uma compreensão mais profunda do propósito da vida",
    practicalImpact: [
      "Você cria rotinas e sistemas que realmente sustentam seu bem-estar",
      "Consegue ensinar outros através do exemplo de uma vida organizada",
      "Equilibra disciplina com sabedoria, evitando rigidez desnecessária"
    ]
  },
  poeta: {
    meaning: "Habilidade de expressar sua transformação interior de forma criativa",
    whenItAppears: "Emerge quando sua jornada pessoal está pedindo para ser expressa e compartilhada através de formas criativas",
    practicalImpact: [
      "Você encontra na expressão criativa uma forma natural de processar emoções",
      "Tem facilidade para transformar experiências pessoais em arte ou comunicação",
      "Consegue inspirar outros através da beleza que cria"
    ]
  }
};

const triplePracticalMapping = {
  profeta: {
    meaning: "Capacidade madura de transformar visão interior em mensagem que ajuda outros",
    whenItAppears: "Surge quando você desenvolveu sabedoria suficiente para guiar outros através de sua própria experiência e intuição",
    practicalImpact: [
      "Você consegue ver padrões e possibilidades que outros ainda não percebem",
      "Tem facilidade natural para comunicar ideias transformadoras",
      "As pessoas buscam sua perspectiva em momentos de decisão importante"
    ]
  },
  construtor: {
    meaning: "Habilidade avançada de materializar visões em realidades concretas",
    whenItAppears: "Aparece quando você está integrando todas suas capacidades para criar algo duradouro e significativo no mundo",
    practicalImpact: [
      "Você consegue transformar ideias abstratas em projetos concretos",
      "Tem persistência para levar projetos importantes até o fim",
      "Equilibra sonho com praticidade de forma natural"
    ]
  },
  companheiro: {
    meaning: "Capacidade madura de acompanhar outros sem perder sua própria jornada",
    whenItAppears: "Emerge quando você desenvolveu a habilidade de estar verdadeiramente presente para outros mantendo sua própria integridade",
    practicalImpact: [
      "Você consegue apoiar outros sem assumir seus problemas como seus",
      "Tem presença natural que faz as pessoas se sentirem menos sozinhas",
      "Equilibra dar e receber apoio de forma saudável"
    ]
  },
  sabio_compassivo: {
    meaning: "Integração madura entre sabedoria pessoal e compaixão ativa",
    whenItAppears: "Surge quando você desenvolveu tanto compreensão profunda quanto capacidade prática de aliviar sofrimento",
    practicalImpact: [
      "Você consegue oferecer sabedoria prática que realmente ajuda",
      "Tem presença que naturalmente acalma e orienta",
      "Equilibra compaixão com sabedoria para evitar codependência"
    ]
  }
};

// Função para gerar interpretação textual
const generateTextualInterpretation = (result) => {
  const maxValue = Math.max(...Object.values(result.mainArchetypes));
  const minValue = Math.min(...Object.values(result.mainArchetypes));
  const axisRange = maxValue - minValue;
  
  let archetypeRoot = '';
  for (const [key, value] of Object.entries(result.mainArchetypes)) {
    if (value === maxValue) {
      archetypeRoot = key.charAt(0).toUpperCase() + key.slice(1);
      break;
    }
  }

  const DUALS = ['visionario','encontro','guerreiro','curador','guardiao_sabio','poeta'];
  const TRIPLES = ['profeta','construtor','companheiro','sabio_compassivo'];
  
  // Usar limiar único baseado no motor (combos com level !== 'latente')
  const duplasAtivas = result.combos 
    ? Object.keys(result.combos).filter(k => result.combos[k].level !== 'latente' && DUALS.includes(k))
    : Object.entries(result.dualCombinations).filter(([_, score]) => score >= 7).map(([key]) => key);

  const triplasAtivas = result.combos
    ? Object.keys(result.combos).filter(k => result.combos[k].level !== 'latente' && TRIPLES.includes(k))
    : Object.entries(result.tripleCombinations).filter(([_, score]) => score >= 4).map(([key]) => key);

  const archetypeEssences = {
    'Amante': 'um coração movido pelo afeto, pela entrega e pela beleza das conexões',
    'Alquimista': 'uma alma transformadora que aprende através da dor e do sentido',
    'Guardiao': 'uma força estabilizadora e protetora, que dá forma e segurança à vida',
    'Peregrino': 'uma busca contínua de sentido e movimento, que se guia pela curiosidade e pela fé'
  };

  const duplaDescriptions = {
    'curador': 'O Curador aprofunda a capacidade de cuidar e regenerar.',
    'visionario': 'O Visionário abre horizontes para a intuição e o propósito.',
    'poeta': 'O Poeta revela o mundo interno através da sensibilidade e expressão simbólica.',
    'guerreiro': 'O Guerreiro traz coragem e direção ao sentimento.',
    'encontro': 'O Peregrino do Encontro une caminho e afeto, encontrando-se no outro.',
    'guardiao_sabio': 'O Guardião Sábio traduz sabedoria em estabilidade e compaixão.'
  };

  const triplaDescriptions = {
    'profeta': 'O Profeta expressa lucidez espiritual e visão do que está por vir.',
    'construtor': 'O Construtor estrutura o propósito em ação concreta.',
    'companheiro': 'O Companheiro representa vínculos sólidos e aprendizado pela presença.',
    'sabio_compassivo': 'O Sábio Compassivo sintetiza amor, lucidez e serenidade — a consciência madura do coração.'
  };

  const phenomenologicalDescriptions = {
    'Amante': 'profundidade emocional, empatia e poder de conexão',
    'Alquimista': 'sabedoria forjada pela experiência e o dom de ressignificar',
    'Guardiao': 'presença sólida, ética e responsabilidade afetiva',
    'Peregrino': 'vontade de aprender, experimentar e evoluir'
  };

  const developmentNeeds = {
    'Amante': 'estrutura e centramento para sustentar o afeto',
    'Alquimista': 'abrir-se mais ao prazer e à leveza',
    'Guardiao': 'permitir-se sentir e flexibilizar o controle',
    'Peregrino': 'assentar-se e encontrar propósito no caminho'
  };

  let text = `O padrão revelado neste resultado indica um arquétipo raiz de **${archetypeRoot}**, cuja essência se manifesta como ${archetypeEssences[archetypeRoot] || 'uma força única do coração'}. `;

  if (result.isCoracaoInteiro) {
    text += `Há aqui um **Coração Inteiro**, sinal de integração madura entre ação, emoção, corpo e sentido — uma harmonia rara entre os quatro eixos. `;
  } else {
    text += `Ainda não há Coração Inteiro, pois há desequilíbrio entre o eixo de maior força e o mais atenuado (diferença de ${axisRange} pontos). Isso indica um coração em movimento, expandindo-se para integrar dimensões ainda não plenamente amadurecidas. `;
  }

  if (duplasAtivas.length > 0) {
    text += `As duplas ativas — **${duplasAtivas.join(', ')}** — representam pontes vivas entre forças do ser. `;
    duplasAtivas.forEach(dupla => {
      if (duplaDescriptions[dupla]) {
        text += `${duplaDescriptions[dupla]} `;
      }
    });
  }

  if (triplasAtivas.length > 0) {
    text += `As triplas ativas — **${triplasAtivas.join(', ')}** — mostram núcleos complexos de integração. `;
    triplasAtivas.forEach(tripla => {
      if (triplaDescriptions[tripla]) {
        text += `${triplaDescriptions[tripla]} `;
      }
    });
  }

  text += `Fenomenologicamente, este coração expressa ${phenomenologicalDescriptions[archetypeRoot] || 'características únicas'}. A força maior se manifesta na ${archetypeRoot}, mas o amadurecimento futuro pede ${developmentNeeds[archetypeRoot] || 'maior integração das polaridades'}. `;

  const integrationStatus = result.isCoracaoInteiro ? 'plena integração' : 'fase de integração';
  text += `O panorama geral mostra um coração em ${integrationStatus} — buscando unir ação, emoção, corpo e alma em uma consciência mais inteira. Quando ${archetypeRoot} integrar suas polaridades complementares, o coração se tornará **Coração Inteiro**, ponto de convergência entre sabedoria, afeto e presença viva.`;

  return text;
};

// Função para gerar interpretação prática
const generatePracticalInterpretation = (result) => {
  const maxValue = Math.max(...Object.values(result.mainArchetypes));
  let rootArchetype = '';
  
  for (const [key, value] of Object.entries(result.mainArchetypes)) {
    if (value === maxValue) {
      rootArchetype = key;
      break;
    }
  }

  const mainData = practicalMapping[rootArchetype] || practicalMapping.peregrino;

  const DUALS_KEYS = ['visionario','encontro','guerreiro','curador','guardiao_sabio','poeta'];
  const TRIPLES_KEYS = ['profeta','construtor','companheiro','sabio_compassivo'];
  
  // Usar limiar único baseado no motor (combos com level !== 'latente')
  const activeDuals = result.combos 
    ? Object.keys(result.combos).filter(k => result.combos[k].level !== 'latente' && DUALS_KEYS.includes(k))
    : Object.entries(result.dualCombinations).filter(([_, score]) => score >= 7).map(([key]) => key);
    
  const activeTriplas = result.combos
    ? Object.keys(result.combos).filter(k => result.combos[k].level !== 'latente' && TRIPLES_KEYS.includes(k))
    : Object.entries(result.tripleCombinations).filter(([_, score]) => score >= 4).map(([key]) => key);

  const activeDualsExplanation = activeDuals
    .filter(key => dualPracticalMapping[key])
    .map(key => ({ key, ...dualPracticalMapping[key] }));

  const activeTriplesExplanation = activeTriplas
    .filter(key => triplePracticalMapping[key])
    .map(key => ({ key, ...triplePracticalMapping[key] }));

  return {
    mainStrengths: mainData.strengths,
    mainChallenges: mainData.challenges,
    developmentAreas: mainData.developmentAreas,
    advice: mainData.practicalAdvice,
    activeDualsExplanation,
    activeTriplesExplanation
  };
};

// ============ INTERPRETAÇÃO ACESSÍVEL (VERSÃO ACOLHEDORA) ============
const archetypeQualities = {
  peregrino: {
    jeito_principal: 'presença e movimento',
    qualidades_principais: 'curiosidade, abertura e busca',
    intencao_vital: 'descobrir novos caminhos e crescer com cada experiência',
    manifestacao_positiva: 'renovar sua energia e encontrar sentido no movimento da vida',
    aprendizado_futuro: 'aprender a permanecer e criar raízes onde você já está',
    modo_de_percepcao: 'atenta ao que está por vir',
    beneficio: 'antecipar mudanças e se adaptar com facilidade',
    estilo_de_acao: 'as coisas explorando e experimentando',
    efeito_no_outro: 'coragem para também se moverem',
    processo_de_crescimento: 'encontrar seu lugar no mundo',
    pratica_de_integracao: 'equilibra movimento com momentos de quietude'
  },
  guardiao: {
    jeito_principal: 'ação e corpo',
    qualidades_principais: 'firmeza, cuidado e responsabilidade',
    intencao_vital: 'criar estruturas que protegem e nutrem a vida',
    manifestacao_positiva: 'oferecer segurança e estabilidade para você e os outros',
    aprendizado_futuro: 'aprender a confiar no fluxo natural das coisas',
    modo_de_percepcao: 'concreta e prática',
    beneficio: 'criar soluções sólidas e confiáveis',
    estilo_de_acao: 'as coisas com constância e determinação',
    efeito_no_outro: 'confiança e sensação de amparo',
    processo_de_crescimento: 'construir bases sólidas para sua vida',
    pratica_de_integracao: 'une organização com flexibilidade'
  },
  amante: {
    jeito_principal: 'sentimento e vínculo',
    qualidades_principais: 'ternura, entrega e conexão',
    intencao_vital: 'criar pontes de afeto e comunhão com outros seres',
    manifestacao_positiva: 'transformar solidão em presença compartilhada',
    aprendizado_futuro: 'aprender a amar sem se perder no outro',
    modo_de_percepcao: 'sensível e empática',
    beneficio: 'compreender profundamente o coração humano',
    estilo_de_acao: 'as coisas com entrega e disponibilidade',
    efeito_no_outro: 'acolhimento e sensação de serem amados',
    processo_de_crescimento: 'aprofundar seus vínculos mais importantes',
    pratica_de_integracao: 'equilibra doação com autocuidado'
  },
  alquimista: {
    jeito_principal: 'inspiração e alma',
    qualidades_principais: 'sabedoria, profundidade e transformação',
    intencao_vital: 'encontrar sentido mesmo nas experiências mais difíceis',
    manifestacao_positiva: 'transformar dificuldades em aprendizado e luz',
    aprendizado_futuro: 'aprender a viver mais no corpo e no presente',
    modo_de_percepcao: 'profunda e simbólica',
    beneficio: 'encontrar significados ocultos nas experiências',
    estilo_de_acao: 'as coisas com reflexão e intenção',
    efeito_no_outro: 'inspiração para também buscarem sentido',
    processo_de_crescimento: 'integrar sabedoria com presença viva',
    pratica_de_integracao: 'equilibra reflexão com ação concreta'
  }
};

const generateAccessibleInterpretation = (result) => {
  // Determinar arquétipo raiz
  let rootArchetype = '';
  const maxValue = Math.max(...Object.values(result.mainArchetypes));
  const minValue = Math.min(...Object.values(result.mainArchetypes));
  
  for (const [key, value] of Object.entries(result.mainArchetypes)) {
    if (value === maxValue) {
      rootArchetype = key;
      break;
    }
  }

  // Determinar arquétipo mais fraco
  let weakestArchetype = '';
  for (const [key, value] of Object.entries(result.mainArchetypes)) {
    if (value === minValue) {
      weakestArchetype = key;
      break;
    }
  }

  const rootQualities = archetypeQualities[rootArchetype] || archetypeQualities.peregrino;
  const range = maxValue - minValue;

  // Buscar dados do arquétipo DOMINANTE real (não só o raiz)
  const dominantKey = result.dominantArchetype || rootArchetype;
  const dominantData = archetypeResults[dominantKey];

  // Camada Emocional — usa conteúdo personalizado do ARQUÉTIPO DOMINANTE
  // (description já aparece no card principal, aqui usamos os textos complementares)
  const camada_emocional = {
    introducao_suave: dominantData?.prosAcolhedor || `Você tende a viver com ${rootQualities.qualidades_principais}, buscando ${rootQualities.intencao_vital}.`,
    descricao_essencial: dominantData?.contrasAcolhedor || `Essa força te ajuda a ${rootQualities.manifestacao_positiva}, mesmo quando a vida pede pausa e recomeço.`,
    mensagem_inspiradora: dominantData?.conselhoAcolhedor || `Talvez o próximo passo seja ${rootQualities.aprendizado_futuro}, para que seu coração viva com ainda mais equilíbrio e paz.`,
    praticas: dominantData?.praticasAcolhedoras || []
  };

  // Camada Visual - Estado de Integração
  let estado_integracao;
  if (range <= 3) {
    estado_integracao = 'integro';
  } else if (range <= 8) {
    estado_integracao = 'quase_integro';
  } else {
    estado_integracao = 'fragmentado';
  }

  const mapa_do_coracao = {
    presenca: result.mainArchetypes.peregrino,
    acao: result.mainArchetypes.guardiao,
    alma: result.mainArchetypes.alquimista,
    sentimento: result.mainArchetypes.amante
  };

  const simbolos_ativos = [];
  Object.entries(mapa_do_coracao).forEach(([key, value]) => {
    if (value >= 30) {
      const simbolos = {
        presenca: '🌿',
        acao: '🔥',
        alma: '🌙',
        sentimento: '💧'
      };
      simbolos_ativos.push(simbolos[key]);
    }
  });

  // Camada de Clareza
  const camada_clareza = {
    como_voce_sente: `Você costuma perceber as situações de forma ${rootQualities.modo_de_percepcao}. Isso te ajuda a ${rootQualities.beneficio}.`,
    como_voce_age: `Quando você age, costuma fazer ${rootQualities.estilo_de_acao}, e isso inspira nos outros ${rootQualities.efeito_no_outro}.`,
    o_que_esta_crescendo: `Neste momento, está florescendo em você o desejo de ${rootQualities.processo_de_crescimento}.`,
    o_que_te_equilibra: `Você se sente melhor quando ${rootQualities.pratica_de_integracao}.`
  };

  // Camada Evolutiva
  const termMapping = {
    peregrino: 'presença e movimento',
    guardiao: 'ação e corpo',
    amante: 'sentimento e vínculo',
    alquimista: 'inspiração e alma'
  };

  let tipo_evolutivo, mensagem_evolutiva, sugestao_pratica;

  if (result.isCoracaoInteiro) {
    tipo_evolutivo = 'estabilidade';
    mensagem_evolutiva = 'Seu modo de viver se mantém firme, sustentando equilíbrio e serenidade.';
    sugestao_pratica = 'Continue cultivando essa harmonia interior através de momentos diários de gratidão.';
  } else if (range > 15) {
    tipo_evolutivo = 'revisao';
    const area_cuidar = termMapping[weakestArchetype] || weakestArchetype;
    mensagem_evolutiva = `Talvez seja um bom momento para cuidar mais de ${area_cuidar}, com gentileza e atenção.`;
    
    const sugestoes = {
      peregrino: 'Respire fundo e desacelere por alguns minutos ao dia.',
      guardiao: 'Escolha uma coisa pequena para realizar e finalize com calma.',
      alquimista: 'Silencie um pouco. Escreva o que te move de verdade.',
      amante: 'Converse com alguém que te faz bem, mesmo que por poucos minutos.'
    };
    sugestao_pratica = sugestoes[weakestArchetype] || 'Dedique alguns minutos diários para estar consigo mesmo(a).';
  } else {
    tipo_evolutivo = 'crescimento';
    const qualidade_expandindo = rootQualities.qualidades_principais.split(',')[0];
    mensagem_evolutiva = `Você está se tornando mais ${qualidade_expandindo} — um sinal de amadurecimento e presença.`;
    sugestao_pratica = 'Continue nutrindo esse crescimento com pequenas ações diárias alinhadas com seu coração.';
  }

  return {
    camada_emocional,
    camada_visual: {
      mapa_do_coracao,
      estado_integracao,
      simbolos_ativos
    },
    camada_clareza,
    camada_evolutiva: {
      tipo: tipo_evolutivo,
      mensagem: mensagem_evolutiva,
      sugestao_pratica
    }
  };
};

// ============ COMPONENTE PRINCIPAL ============
const HeartSeasonTest = ({ onClose }) => {
  const [phase, setPhase] = useState('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [sessionId] = useState(() => uuidv4());
  const [viewMode, setViewMode] = useState('acolhedora');
  
  const [saveFormData, setSaveFormData] = useState({
    nome_identificador: '',
    email: '',
    lgpd_consent: false
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Estados para a tela intermediária
  const [collectName, setCollectName] = useState('');
  const [collectEmail, setCollectEmail] = useState('');
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const [showEvolucao, setShowEvolucao] = useState(false);
  const [collectError, setCollectError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sharingBussola, setSharingBussola] = useState(false);
  const [showDemoMode, setShowDemoMode] = useState(false);
  const [showJornada, setShowJornada] = useState(false);
  const bussolaRef = useRef(null);

  const handleDownloadBussola = async () => {
    const element = bussolaRef.current;
    if (!element) return;
    setSharingBussola(true);
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#1e1b4b',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'minha-bussola-viva.png';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao gerar imagem:', err);
    } finally {
      setSharingBussola(false);
    }
  };

  const handleShareLink = async () => {
    const shareText = `Descobri meu arquétipo emocional: ${result?.dominantArchetypeName || result?.archetypeData?.titulo || ''}. Descubra o seu em:`;
    const shareUrl = 'https://estacoes.ocoracaodetodasascoisas.com';
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Minha Bússola Viva', text: shareText, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        alert('Link copiado!');
      }
    } catch (_) {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        alert('Link copiado!');
      } catch (__) {}
    }
  };

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(TEST_URL)}`;

  const handleStartQuiz = () => setPhase('quiz');
  
  // ============ MODO DE TESTE RÁPIDO PARA DEMONSTRAÇÕES ============
  const handleQuickDemo = (demoType) => {
    const demoProfiles = {
      // Perfil balanceado (Coração Inteiro)
      // Perfil balanceado (Coração Inteiro)
      balanced: () => {
        const demoAnswers = {};
        questions.forEach(q => {
          if (q.kind === 'attention') demoAnswers[q.id] = q.expect;
          else demoAnswers[q.id] = q.reverse ? 2 : 4; // diretos altos, reversos baixos -> Coração Inteiro
        });
        return demoAnswers;
      },
      // Perfil Peregrino dominante
      peregrino: () => {
        const demoAnswers = {};
        questions.forEach(q => {
          if (q.kind === 'attention') demoAnswers[q.id] = q.expect;
          else if (q.category === 'peregrino' && !q.reverse) demoAnswers[q.id] = 5;
          else if (q.category === 'peregrino' && q.reverse) demoAnswers[q.id] = 1;
          else if (['visionario', 'encontro', 'profeta'].includes(q.category)) demoAnswers[q.id] = 4;
          else demoAnswers[q.id] = 2;
        });
        return demoAnswers;
      },
      // Perfil Guardião dominante
      guardiao: () => {
        const demoAnswers = {};
        questions.forEach(q => {
          if (q.kind === 'attention') demoAnswers[q.id] = q.expect;
          else if (q.category === 'guardiao' && !q.reverse) demoAnswers[q.id] = 5;
          else if (q.category === 'guardiao' && q.reverse) demoAnswers[q.id] = 1;
          else if (['guerreiro', 'curador', 'construtor'].includes(q.category)) demoAnswers[q.id] = 4;
          else demoAnswers[q.id] = 2;
        });
        return demoAnswers;
      },
      // Perfil Amante dominante  
      amante: () => {
        const demoAnswers = {};
        questions.forEach(q => {
          if (q.kind === 'attention') demoAnswers[q.id] = q.expect;
          else if (q.category === 'amante' && !q.reverse) demoAnswers[q.id] = 5;
          else if (q.category === 'amante' && q.reverse) demoAnswers[q.id] = 1;
          else if (['encontro', 'curador', 'poeta', 'companheiro'].includes(q.category)) demoAnswers[q.id] = 4;
          else demoAnswers[q.id] = 2;
        });
        return demoAnswers;
      },
      // Perfil Alquimista dominante
      alquimista: () => {
        const demoAnswers = {};
        questions.forEach(q => {
          if (q.kind === 'attention') demoAnswers[q.id] = q.expect;
          else if (q.category === 'alquimista' && !q.reverse) demoAnswers[q.id] = 5;
          else if (q.category === 'alquimista' && q.reverse) demoAnswers[q.id] = 1;
          else if (['visionario', 'poeta', 'guardiao_sabio', 'profeta', 'sabio_compassivo'].includes(q.category)) demoAnswers[q.id] = 4;
          else demoAnswers[q.id] = 2;
        });
        return demoAnswers;
      },
      // Perfil aleatório (mistura)
      random: () => {
        const demoAnswers = {};
        questions.forEach(q => {
          if (q.kind === 'attention') demoAnswers[q.id] = q.expect;
          else demoAnswers[q.id] = Math.floor(Math.random() * 5) + 1;
        });
        return demoAnswers;
      }
    };
    
    const demoAnswers = demoProfiles[demoType]();
    setAnswers(demoAnswers);
    setCollectName('Demonstração');
    setLgpdConsent(true);
    
    // Para modo demo, ir DIRETO para o resultado (pular coleta)
    const r = computeResult(demoAnswers, questions);
    setResult({ ...r, archetypeData: archetypeResults[r.displayKey] });
    setPhase('result');
  };

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 400);
    } else {
      // Calcular resultado e ir para tela de coleta de dados
      prepareResult(newAnswers);
    }
  };

  const prepareResult = (allAnswers) => {
    const r = computeResult(allAnswers, questions);
    setResult({ ...r, archetypeData: archetypeResults[r.displayKey] });
    setPhase('collect_info');
  };

  // Validação de email
  const isValidEmail = (email) => {
    if (!email) return true; // Email vazio é válido (opcional)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Verificar se pode habilitar o botão
  const canSubmitCollectForm = () => {
    if (!collectName.trim()) return false;
    if (collectEmail && !isValidEmail(collectEmail)) return false;
    if (!lgpdConsent) return false;
    return true;
  };

  // Handler para salvar e ver resultado
  const handleSaveAndShowResult = async () => {
    setCollectError('');
    setEmailError('');
    
    if (!collectName.trim()) {
      return;
    }
    
    if (collectEmail && !isValidEmail(collectEmail)) {
      setEmailError('Por favor, insira um e-mail válido ou deixe em branco.');
      return;
    }
    
    setSaving(true);
    
    try {
      const payload = {
        session_id: sessionId,
        respostas: answers,
        score_peregrino: result.mainArchetypes.peregrino,
        score_guardiao: result.mainArchetypes.guardiao,
        score_amante: result.mainArchetypes.amante,
        score_alquimista: result.mainArchetypes.alquimista,
        arquetipo_dominante: result.dominantArchetype,
        combinacoes_duais: result.dualCombinations,
        combinacoes_triplas: result.tripleCombinations,
        is_coracao_inteiro: result.isCoracaoInteiro || false,
        nome_identificador: collectName.trim(),
        email: collectEmail.trim() || null,
        lgpd_consent: lgpdConsent,
        origem: "estacoes.ocoracaodetodasascoisas.com"
      };
      
      // Usar AbortController com timeout de 8 segundos
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      const response = await fetch(`${BACKEND_URL}/api/test-results/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        // Atualizar saveFormData para manter consistência
        setSaveFormData({
          nome_identificador: collectName.trim(),
          email: collectEmail.trim() || '',
          lgpd_consent: true
        });
        setSaved(true);
        
        // Disparar evento do Meta Pixel
        trackCompleteTest(result.dominantArchetype, sessionId);
        
        setPhase('result');
      } else {
        // Salvar falhou, mas ainda mostra o resultado
        trackCompleteTest(result.dominantArchetype, sessionId);
        setPhase('result');
      }
    } catch (error) {
      // Mesmo com erro de rede ou timeout, mostrar o resultado ao usuário
      trackCompleteTest(result.dominantArchetype, sessionId);
      setPhase('result');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveResult = async () => {
    if (!saveFormData.lgpd_consent) {
      toast({ title: "Consentimento necessário", description: "Por favor, aceite os termos de privacidade.", variant: "destructive" });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        session_id: sessionId,
        respostas: answers,
        score_peregrino: result.mainArchetypes.peregrino,
        score_guardiao: result.mainArchetypes.guardiao,
        score_amante: result.mainArchetypes.amante,
        score_alquimista: result.mainArchetypes.alquimista,
        arquetipo_dominante: result.dominantArchetype,
        combinacoes_duais: result.dualCombinations,
        combinacoes_triplas: result.tripleCombinations,
        is_coracao_inteiro: result.isCoracaoInteiro || false,
        nome_identificador: saveFormData.nome_identificador || null,
        email: saveFormData.email || null,
        lgpd_consent: saveFormData.lgpd_consent,
        origem: "estacoes.ocoracaodetodasascoisas.com"
      };
      
      const response = await fetch(`${BACKEND_URL}/api/test-results/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setSaved(true);
        toast({ title: "Resultado salvo!", description: "Seu resultado foi registrado com sucesso." });
      }
    } catch (error) {
      toast({ title: "Erro ao salvar", description: "Tente novamente.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    const archetype = result.archetypeData;
    const shareText = `Descobri que sou ${archetype.title} no Teste das Estações do Coração! ${archetype.emoji}\n\nFaça você também: ${TEST_URL}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: `Meu resultado: ${archetype.title}`, text: shareText });
      } catch (err) {
        await navigator.clipboard.writeText(shareText);
        toast({ title: "Link copiado!", description: "Compartilhe com seus amigos." });
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast({ title: "Link copiado!", description: "Compartilhe com seus amigos." });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-results/pdf/${sessionId}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `resultado_coracao_${saveFormData.nome_identificador || 'teste'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast({ title: "PDF baixado!", description: "Seu resultado foi salvo em PDF." });
      } else {
        toast({ title: "Erro", description: "Não foi possível gerar o PDF.", variant: "destructive" });
      }
    } catch (err) {
      console.error('PDF download error:', err);
      toast({ title: "Erro", description: "Não foi possível baixar o PDF.", variant: "destructive" });
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(TEST_URL);
      toast({ title: "Link copiado!", description: "Compartilhe com seus amigos." });
    } catch (err) {
      toast({ title: "Erro", description: "Não foi possível copiar o link.", variant: "destructive" });
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  // ============ TELA INICIAL ============
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#162544] to-[#1a2d4d] relative overflow-hidden">
        {/* Animated Stars Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white animate-twinkle"
              style={{
                width: Math.random() * 3 + 1 + 'px',
                height: Math.random() * 3 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                animationDelay: Math.random() * 5 + 's',
                animationDuration: Math.random() * 3 + 2 + 's',
                opacity: Math.random() * 0.7 + 0.3,
              }}
            />
          ))}
          {/* Glowing orbs */}
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/15 rounded-full blur-[120px] animate-pulse-slow" />
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-indigo-500/15 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px]" />
        </div>

        <div className="container mx-auto px-4 py-8 relative z-10">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="mb-8">
              {/* Animated Heart Icon */}
              <div className="relative inline-flex items-center justify-center mb-6">
                <div className="absolute inset-0 w-24 h-24 bg-gradient-to-br from-rose-500/30 to-purple-500/30 rounded-full blur-xl animate-pulse-slow" />
                <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-full border border-white/20 shadow-2xl">
                  <Heart className="w-12 h-12 text-white drop-shadow-lg animate-heartbeat" />
                </div>
                {/* Orbiting particles */}
                <div className="absolute w-32 h-32 animate-spin-slow">
                  <div className="absolute top-0 left-1/2 w-2 h-2 bg-amber-400 rounded-full blur-[1px]" />
                  <div className="absolute bottom-0 left-1/2 w-1.5 h-1.5 bg-cyan-400 rounded-full blur-[1px]" />
                </div>
              </div>
              
              {/* Title with shimmer effect */}
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
                Estações do
                <span className="relative ml-3">
                  <span className="bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 bg-clip-text text-transparent animate-shimmer bg-[length:200%_100%]">
                    Coração
                  </span>
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed mb-2">
                Descubra seu arquétipo interior através de uma jornada de autoconhecimento profundo
              </p>
              <p className="text-sm text-amber-200/60 max-w-xl mx-auto">
                Baseado no livro "O Coração de Todas as Coisas"
              </p>
            </div>

            {/* CTA Button with glow effect */}
            <button
              onClick={handleStartQuiz}
              className="group relative inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white font-bold text-lg rounded-2xl transition-all duration-500 shadow-[0_0_40px_rgba(245,158,11,0.3)] hover:shadow-[0_0_60px_rgba(245,158,11,0.5)] transform hover:scale-105 animate-glow-pulse"
              data-testid="start-quiz-button"
              style={{ border: 'none' }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
              <Play className="relative w-6 h-6" />
              <span className="relative">Iniciar Minha Jornada</span>
            </button>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://ocoracaodetodasascoisas.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white/5 backdrop-blur-sm text-white/80 font-medium rounded-xl hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-white/20"
                data-testid="visit-site-button"
              >
                <BookOpen className="w-5 h-5 group-hover:text-amber-300 transition-colors" />
                <span>Conhecer o Livro</span>
              </a>
              
              <a
                href="/encontro"
                className="group inline-flex items-center justify-center space-x-2 px-6 py-3 bg-rose-500/10 backdrop-blur-sm text-rose-200 font-medium rounded-xl hover:bg-rose-500/20 transition-all duration-300 border border-rose-500/20 hover:border-rose-400/40"
                data-testid="bussola-encontro-button"
              >
                <span>💞</span>
                <span>Bússola do Encontro (a dois)</span>
              </a>
              
              <button
                onClick={() => setShowDemoMode(!showDemoMode)}
                className="group inline-flex items-center justify-center space-x-2 px-6 py-3 bg-purple-500/10 backdrop-blur-sm text-purple-200 font-medium rounded-xl hover:bg-purple-500/20 transition-all duration-300 border border-purple-500/20 hover:border-purple-400/40"
                data-testid="demo-mode-toggle"
                style={{ border: '1px solid rgba(168, 85, 247, 0.2)' }}
              >
                <Sparkles className="w-5 h-5 group-hover:animate-spin" />
                <span>Modo Demonstração</span>
              </button>
            </div>
            
            {/* Painel de Demonstração Rápida */}
            {showDemoMode && (
              <div className="mt-6 bg-gradient-to-br from-amber-900/30 to-orange-900/20 backdrop-blur-sm rounded-2xl p-6 border border-amber-500/30 max-w-2xl mx-auto animate-fade-in">
                <h3 className="text-lg font-bold text-amber-200 mb-2 text-center">⚡ Demonstração Rápida</h3>
                <p className="text-white/70 text-sm text-center mb-4">Escolha um perfil para ver o resultado instantaneamente</p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'peregrino', label: 'O Peregrino', desc: 'Busca e Movimento', color: 'from-blue-500 to-indigo-500' },
                    { key: 'guardiao', label: 'O Guardião', desc: 'Corpo e Raiz', color: 'from-purple-500 to-violet-500' },
                    { key: 'amante', label: 'O Amante', desc: 'Vínculo e Afeto', color: 'from-pink-500 to-rose-500' },
                    { key: 'alquimista', label: 'O Alquimista', desc: 'Sentido e Transformação', color: 'from-amber-500 to-orange-500' },
                    { key: 'balanced', label: 'Coração Inteiro', desc: 'Equilíbrio Total', color: 'from-teal-500 to-emerald-500' },
                    { key: 'random', label: 'Aleatório', desc: 'Resultado Surpresa', color: 'from-gray-500 to-slate-500' },
                  ].map(({ key, label, desc, color }) => (
                    <button
                      key={key}
                      onClick={() => handleQuickDemo(key)}
                      className={`p-3 rounded-xl bg-gradient-to-br ${color} hover:opacity-90 transition-all duration-200 text-left group`}
                      style={{ border: 'none' }}
                      data-testid={`demo-${key}`}
                    >
                      <span className="text-white font-semibold text-sm block">{label}</span>
                      <span className="text-white/70 text-xs">{desc}</span>
                    </button>
                  ))}
                </div>
                
                {/* Demonstração Bússola do Encontro */}
                <div className="mt-4 pt-4 border-t border-amber-500/20">
                  <p className="text-rose-200/80 text-xs text-center mb-3">💞 Demonstrar a Bússola do Encontro (para casais)</p>
                  <a 
                    href="/encontro?demo=true"
                    className="block w-full p-3 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 hover:opacity-90 transition-all duration-200 text-center"
                    style={{ textDecoration: 'none' }}
                    data-testid="demo-encontro"
                  >
                    <span className="text-white font-semibold text-sm block">💕 Bússola do Encontro</span>
                    <span className="text-white/70 text-xs">Ver resultado de casal</span>
                  </a>
                </div>
                
                <p className="text-amber-200/50 text-xs text-center mt-4">
                  💡 Ideal para apresentações e demonstrações do sistema
                </p>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="space-y-10 mb-12">
            {/* Feature Cards with glass-morphism */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { icon: '✨', title: '63 Perguntas', desc: 'Explorando quatro dimensões da experiência humana', color: 'from-blue-500 to-cyan-500' },
                { icon: '🔮', title: '15 Arquétipos', desc: 'Principais, combinações e Coração Inteiro', color: 'from-purple-500 to-pink-500' },
                { icon: '🌱', title: 'Orientação', desc: 'Práticas específicas para seu momento', color: 'from-emerald-500 to-teal-500' }
              ].map(({ icon, title, desc, color }, idx) => (
                <div 
                  key={title}
                  className="group relative bg-white/[0.03] backdrop-blur-md rounded-2xl p-6 border border-white/10 text-center hover:bg-white/[0.06] transition-all duration-500 hover:border-white/20 hover:-translate-y-1"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`} />
                  <div className={`relative w-14 h-14 bg-gradient-to-br ${color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <span className="text-2xl">{icon}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-200 transition-colors">{title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            {/* Archetypes Preview - COM EFEITOS PREMIUM */}
            <div className="relative bg-gradient-to-br from-slate-900/80 via-indigo-900/40 to-slate-900/80 backdrop-blur-md rounded-3xl p-8 border border-amber-500/20 max-w-5xl mx-auto overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
              
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 text-center">Os Arquétipos do Coração</h2>
              <p className="text-amber-200/60 text-sm text-center mb-8">As quatro estações que habitam cada um de nós</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {[
                  { key: 'peregrino', glow: 'shadow-blue-500/20 hover:shadow-blue-500/40' },
                  { key: 'guardiao', glow: 'shadow-purple-500/20 hover:shadow-purple-500/40' },
                  { key: 'amante', glow: 'shadow-pink-500/20 hover:shadow-pink-500/40' },
                  { key: 'alquimista', glow: 'shadow-amber-500/20 hover:shadow-amber-500/40' }
                ].map(({ key, glow }) => (
                  <div key={key} className="group">
                    <div className={`relative overflow-hidden rounded-2xl border-2 border-white/10 group-hover:border-amber-400/50 transition-all duration-500 shadow-xl ${glow} aspect-[3/4] group-hover:-translate-y-2`}>
                      <img 
                        src={`/images/arquetipos/${key}.png`}
                        alt={key}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 ease-out"
                      />
                      {/* Shine effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 translate-x-[-100%] group-hover:translate-x-[100%]" style={{ transition: 'transform 0.7s ease-out, opacity 0.3s' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-white mb-4 text-center">Como Funciona</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { num: '1', title: 'Responda', desc: 'Avalie cada afirmação de 1 a 5' },
                  { num: '2', title: 'Descubra', desc: 'Seu arquétipo será revelado' },
                  { num: '3', title: 'Reflita', desc: 'Receba orientações práticas' }
                ].map(({ num, title, desc }) => (
                  <div key={num} className="text-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold text-sm">{num}</span>
                    </div>
                    <h3 className="text-white font-medium text-sm mb-1">{title}</h3>
                    <p className="text-white/70 text-xs">{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* QR Code Section - NO FINAL DA PÁGINA */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-center max-w-md mx-auto">
              <div className="mb-4">
                <QrCode className="w-8 h-8 text-white mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-white">Compartilhe o Teste</h3>
                <p className="text-white/70 text-sm">Escaneie ou compartilhe o link</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 mb-4 inline-block">
                <img 
                  src={qrCodeUrl} 
                  alt="QR Code para o teste" 
                  className="w-40 h-40 mx-auto"
                />
              </div>
              
              <p className="text-white/60 text-xs mb-4 break-all">{TEST_URL}</p>
              
              <button
                onClick={copyLink}
                style={{ border: 'none' }}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors mx-auto text-sm"
              >
                <Share2 className="w-4 h-4" />
                <span>Copiar Link</span>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-white/50">
            <p className="text-sm italic">
              "O coração é feito de estações, e cada uma de suas faces carrega um arranjo."
            </p>
            <p className="text-xs mt-2">
              Baseado no livro "O Coração de Todas as Coisas" — Autor Magan
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============ TELA DO TESTE ============
  if (phase === 'quiz') {
    const currentQ = questions[currentQuestion];
    const currentValue = answers[currentQ.id] || 0;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-[#0a1628]">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8 relative">
            <button
              onClick={() => setPhase('intro')}
              className="absolute left-0 top-0 flex items-center space-x-2 text-white/70 hover:text-white transition-colors"
              style={{ border: 'none', background: 'transparent' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            
            <h1 className="text-xl md:text-2xl font-bold text-white mb-1">
              Teste das Estações do Coração
            </h1>
            <p className="text-white/70 text-sm">
              Responda com sinceridade
            </p>
          </div>

          {/* Progress bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative bg-white/20 rounded-full h-2 mb-2" style={{ isolation: 'isolate' }}>
              <div 
                className="absolute left-0 top-0 bg-white h-full rounded-full"
                style={{ 
                  width: `${progress}%`,
                  transition: 'width 0.3s ease-out',
                  minWidth: progress > 0 ? '8px' : '0'
                }}
              />
            </div>
            <p className="text-center text-white/60 text-sm">
              {Math.round(progress)}% concluído
            </p>
          </div>

          {/* Question Card */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="text-center mb-2 text-white/50 text-sm">
                Pergunta {currentQuestion + 1} de {questions.length}
              </div>
              <p className="text-white text-lg md:text-xl leading-relaxed text-center">
                {currentQ.text}
              </p>
            </div>
          </div>

          {/* Answer Options */}
          <div className="max-w-2xl mx-auto space-y-3 mb-8">
            {[
              { value: 1, label: 'Discordo totalmente' },
              { value: 2, label: 'Discordo parcialmente' },
              { value: 3, label: 'Neutro' },
              { value: 4, label: 'Concordo parcialmente' },
              { value: 5, label: 'Concordo totalmente' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleAnswer(value)}
                style={{ border: 'none', outline: 'none' }}
                className={`w-full py-4 px-6 rounded-xl text-left flex items-center transition-all duration-300 ${
                  currentValue === value 
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 scale-[1.02]' 
                    : 'bg-white/10 hover:bg-white/20 backdrop-blur-sm'
                }`}
                data-testid={`answer-option-${value}`}
              >
                <span className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 text-sm font-semibold ${
                  currentValue === value ? 'bg-white text-indigo-600' : 'bg-white/20 text-white'
                }`}>
                  {value}
                </span>
                <span className="text-white">{label}</span>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center max-w-2xl mx-auto">
            <button
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              style={{ border: 'none' }}
              className={`flex items-center space-x-2 px-5 py-2 rounded-xl transition-all ${
                currentQuestion === 0 ? 'text-white/30 cursor-not-allowed' : 'text-white bg-white/10 hover:bg-white/20'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Anterior</span>
            </button>

            {currentQuestion < questions.length - 1 && currentValue > 0 && (
              <button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                style={{ border: 'none' }}
                className="flex items-center space-x-2 px-5 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600"
              >
                <span>Próxima</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============ TELA INTERMEDIÁRIA - COLETA DE DADOS ============
  if (phase === 'collect_info') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-[#0a1628] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            {/* Ícone */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Suas respostas estão prontas!
              </h2>
              <p className="text-white/70 text-sm">
                Informe seu nome para ver o resultado. Se quiser recebê-lo por e-mail, preencha abaixo.
              </p>
            </div>

            {/* Formulário */}
            <div className="space-y-4">
              {/* Campo Nome */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Nome <span className="text-pink-400">*</span>
                </label>
                <input
                  type="text"
                  value={collectName}
                  onChange={(e) => setCollectName(e.target.value)}
                  placeholder="Seu nome"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all"
                  data-testid="collect-name-input"
                />
              </div>

              {/* Campo E-mail */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-1">
                  E-mail <span className="text-white/50">(opcional)</span>
                </label>
                <p className="text-white/40 text-xs mb-2">
                  Usado para acompanhar sua evolução emocional ao refazer o teste
                </p>
                <input
                  type="email"
                  value={collectEmail}
                  onChange={(e) => {
                    setCollectEmail(e.target.value);
                    setEmailError('');
                  }}
                  placeholder="seu@email.com"
                  className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-1 transition-all ${
                    emailError 
                      ? 'border-red-400 focus:border-red-400 focus:ring-red-400' 
                      : 'border-white/20 focus:border-blue-400 focus:ring-blue-400'
                  }`}
                  data-testid="collect-email-input"
                />
                {emailError && (
                  <p className="mt-2 text-red-400 text-sm">{emailError}</p>
                )}
              </div>

              {/* Erro geral */}
              {collectError && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3">
                  <p className="text-red-300 text-sm text-center">{collectError}</p>
                </div>
              )}

              {/* Checkbox LGPD */}
              <label className="flex items-start gap-3 text-white/70 text-sm cursor-pointer select-none">
                <input 
                  type="checkbox" 
                  checked={lgpdConsent} 
                  onChange={(e) => setLgpdConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-blue-500 cursor-pointer"
                  data-testid="lgpd-checkbox"
                />
                <span>Autorizo o uso dos meus dados para fins de pesquisa, conforme a Política de Privacidade.</span>
              </label>

              {/* Botão */}
              <button
                onClick={handleSaveAndShowResult}
                disabled={!canSubmitCollectForm() || saving}
                style={{ border: 'none' }}
                className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                  canSubmitCollectForm() && !saving
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 cursor-pointer'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
                data-testid="save-and-show-result-btn"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Salvar e ver meu resultado</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ TELA DE RESULTADO ============
  if (phase === 'result' && result) {
    const archetype = result.archetypeData;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-[#0a1628] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header com botão de voltar */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-4 mb-4">
              <button
                onClick={() => {
                  setPhase('intro');
                  setCurrentQuestion(0);
                  setAnswers({});
                  setResult(null);
                  setCollectName('');
                  setCollectEmail('');
                  setLgpdConsent(false);
                  setShowDemoMode(false);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white/80 hover:text-white rounded-xl transition-all duration-200 border border-white/20"
                data-testid="back-to-home-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Voltar ao Início</span>
              </button>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Seu Resultado
            </h1>
          </div>

          {/* Main Result Card with Image - ATUALIZADO COM NOVAS ARTES */}
          <div className="bg-gradient-to-br from-indigo-900/50 via-slate-900/60 to-purple-900/50 backdrop-blur-sm rounded-2xl p-6 border border-amber-500/20 mb-6 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Archetype Image - Nova Arte */}
              <div className="flex-shrink-0 relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-transparent rounded-xl blur-xl group-hover:blur-2xl transition-all duration-500" />
                <img 
                  src={`/images/arquetipos/${result.displayKey || result.dominantArchetype}.png`}
                  alt={archetype.title}
                  className="relative w-56 h-auto md:w-64 object-contain rounded-xl border-2 border-amber-500/30 shadow-xl group-hover:border-amber-400/50 transition-all duration-300"
                  onError={(e) => {
                    // Fallback para imagem antiga se nova não existir
                    e.target.src = archetype.image;
                    e.target.onerror = () => { e.target.style.display = 'none'; };
                  }}
                />
              </div>
              
              {/* Archetype Info */}
              <div className="text-center md:text-left flex-grow">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-1 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">{archetype.title}</h2>
                <p className="text-amber-200/70 mb-3 text-lg">{archetype.subtitle}</p>
                
                {result.isCoracaoInteiro && (
                  <div className="inline-block px-4 py-2 bg-gradient-to-r from-amber-500/20 to-purple-500/20 rounded-full mb-3 border border-amber-500/30">
                    <span className="text-amber-200 text-sm font-medium">✨ Integração Completa das Estações</span>
                  </div>
                )}
                
                <p className="text-white/80 leading-relaxed">
                  {archetype.description}
                </p>
              </div>
            </div>
          </div>

          {/* ====== SELO DE CONFIABILIDADE ====== */}
          <div className="mb-6">
            <SeloConfiabilidade confidence={result.confidence} onRefazer={() => {
              setPhase('intro'); setCurrentQuestion(0); setAnswers({}); setResult(null);
            }} />
          </div>

          {/* ====== TRANSIÇÃO (se aplicável) ====== */}
          {result.dominant?.type === 'transition' && <ResultadoTransicao result={result} />}

          {/* ====== CARTA DA ESTAÇÃO ====== */}
          <CartaDaEstacao result={result} nome={collectName} />

          {/* ====== TÍTULO DA SEÇÃO ACOLHEDORA ====== */}
          <div className="flex justify-center mb-8 mt-10">
            <div className="bg-gradient-to-r from-purple-900/50 via-pink-900/50 to-purple-900/50 px-8 py-4 rounded-full border border-purple-500/20">
              <div className="flex items-center gap-3">
                <span className="text-3xl">💝</span>
                <div className="text-center">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-wide">Visão Acolhedora</h2>
                  <p className="text-white/50 text-xs mt-0.5">Reflexões do coração</p>
                </div>
              </div>
            </div>
          </div>

          {/* ====== VERSÃO ACOLHEDORA ====== */}
            {(() => {
              const accessible = generateAccessibleInterpretation(result);
              const estadoMessages = {
                integro: 'O círculo pulsa suavemente em harmonia.',
                quase_integro: 'O círculo respira com leve variação entre as partes.',
                fragmentado: 'Algumas partes brilham mais que outras — sinal de crescimento em andamento.'
              };
              
              return (
                <div className="space-y-6">
                  {/* CAMADA 1: Emocional - A Linguagem do Seu Coração */}
                  <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-2xl">💝</span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                        A Linguagem do Seu Coração
                      </h3>
                    </div>
                    <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-4">
                      <p className="text-white/90 leading-relaxed text-lg">
                        {accessible.camada_emocional.introducao_suave}
                      </p>
                      <p className="text-white/80 leading-relaxed">
                        {accessible.camada_emocional.descricao_essencial}
                      </p>
                      <p className="text-white/90 leading-relaxed italic border-t border-white/10 pt-4">
                        {accessible.camada_emocional.mensagem_inspiradora}
                      </p>
                      {accessible.camada_emocional.praticas?.length > 0 && (
                        <div className="border-t border-white/10 pt-4 space-y-3">
                          <p className="text-white/60 text-sm font-medium uppercase tracking-wider">Práticas sugeridas</p>
                          {accessible.camada_emocional.praticas.map((pratica, i) => (
                            <p key={i} className="text-white/70 text-sm leading-relaxed pl-3 border-l-2 border-purple-500/30">
                              {pratica}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* CAMADA 2: Visual - Mapa do Seu Coração */}
                  <div className="space-y-6">
                    {/* Header estilizado como botão */}
                    <div className="flex justify-center">
                      <div className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-900/60 to-indigo-900/60 border border-purple-500/30 shadow-lg">
                        <span className="text-2xl">💜</span>
                        <div>
                          <h3 className="text-xl font-bold text-white">
                            Mapa do Seu Coração
                          </h3>
                          <p className="text-white/50 text-sm">
                            {estadoMessages[accessible.camada_visual.estado_integracao]}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Gráfico de Pizza + Legenda em caixinhas */}
                    {(() => {
                      const mc = accessible.camada_visual.mapa_do_coracao;
                      
                      // Cores e emojis
                      const CORES = {
                        amante: '#EC4899',
                        alquimista: '#F59E0B', 
                        peregrino: '#22C55E',
                        guardiao: '#A855F7',
                      };
                      const EMOJIS = {
                        amante: '💕',
                        alquimista: '✨', 
                        peregrino: '🌿',
                        guardiao: '🏠',
                      };
                      
                      const segments = [
                        { key: 'amante', label: 'Amante', sublabel: 'Vínculo', score: mc.sentimento },
                        { key: 'alquimista', label: 'Alquimista', sublabel: 'Sentido', score: mc.alma },
                        { key: 'peregrino', label: 'Peregrino', sublabel: 'Movimento', score: mc.presenca },
                        { key: 'guardiao', label: 'Guardião', sublabel: 'Estabilidade', score: mc.acao },
                      ];
                      
                      const total = segments.reduce((sum, s) => sum + s.score, 0);
                      const minScore = Math.min(...segments.map(s => s.score));
                      const maxScore = Math.max(...segments.map(s => s.score));

                      // Calcular ângulos para o gráfico de pizza
                      let currentAngle = 0;
                      const computed = segments.map(seg => {
                        const pct = Math.round((seg.score / 40) * 100);
                        const sliceAngle = (seg.score / total) * 360;
                        const startAngle = currentAngle;
                        const midAngle = currentAngle + sliceAngle / 2;
                        currentAngle += sliceAngle;
                        const isWeakest = seg.score === minScore && segments.filter(s => s.score === minScore).length < 4;
                        const isDominant = seg.score === maxScore;
                        
                        // Posição do texto dentro da fatia
                        const textRadius = 30;
                        const textAngleRad = (midAngle - 90) * Math.PI / 180;
                        const textX = 50 + textRadius * Math.cos(textAngleRad);
                        const textY = 50 + textRadius * Math.sin(textAngleRad);
                        
                        return { ...seg, pct, startAngle, sliceAngle, midAngle, isWeakest, isDominant, textX, textY };
                      });

                      // Função para criar path de fatia de pizza
                      const createSlicePath = (startAngle, sliceAngle, radius = 40) => {
                        const startRad = (startAngle - 90) * Math.PI / 180;
                        const endRad = (startAngle + sliceAngle - 90) * Math.PI / 180;
                        const x1 = 50 + radius * Math.cos(startRad);
                        const y1 = 50 + radius * Math.sin(startRad);
                        const x2 = 50 + radius * Math.cos(endRad);
                        const y2 = 50 + radius * Math.sin(endRad);
                        const largeArc = sliceAngle > 180 ? 1 : 0;
                        return `M 50 50 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
                      };

                      const dominante = computed.find(s => s.isDominant) || computed[0];

                      return (
                        <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
                          {/* Gráfico de Pizza */}
                          <div className="relative w-56 h-56 md:w-64 md:h-64">
                            <svg viewBox="0 0 100 100" className="w-full h-full">
                              {/* Fatias da pizza */}
                              {computed.map(seg => (
                                <path
                                  key={seg.key}
                                  d={createSlicePath(seg.startAngle, seg.sliceAngle)}
                                  fill={seg.isWeakest ? 'rgba(100, 116, 139, 0.3)' : CORES[seg.key]}
                                  opacity={seg.isWeakest ? 0.5 : 0.85}
                                  stroke="rgba(15, 23, 42, 0.5)"
                                  strokeWidth="0.5"
                                  className="transition-all duration-500"
                                />
                              ))}
                              
                              {/* Percentuais dentro de cada fatia */}
                              {computed.map(seg => (
                                <text
                                  key={`pct-${seg.key}`}
                                  x={seg.textX}
                                  y={seg.textY}
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  fill="white"
                                  fontSize="6"
                                  fontWeight="bold"
                                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                                >
                                  {seg.pct}%
                                </text>
                              ))}
                              
                              {/* Círculo central */}
                              <circle cx="50" cy="50" r="18" fill="rgba(15, 23, 42, 0.95)" />
                              <circle cx="50" cy="50" r="17" fill="rgba(30, 41, 59, 0.9)" />
                            </svg>

                            {/* Emoji e nome do dominante no centro */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center">
                                <div className="text-2xl mb-0.5">
                                  {EMOJIS[dominante.key]}
                                </div>
                                <div className="text-lg md:text-xl font-bold text-white">
                                  {dominante.pct}%
                                </div>
                                <div className="text-[10px] text-white/60">
                                  {dominante.label}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Legenda em caixinhas separadas */}
                          <div className="flex flex-col gap-3 w-full max-w-sm">
                            {computed.map(seg => (
                              <div 
                                key={seg.key}
                                className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                                  seg.isDominant 
                                    ? 'bg-slate-700/60 border-slate-500/40' 
                                    : 'bg-slate-800/30 border-slate-700/30'
                                }`}
                                data-testid={`legend-${seg.key}`}
                              >
                                {/* Indicador circular colorido */}
                                <div 
                                  className="w-4 h-4 rounded-full flex-shrink-0"
                                  style={{ 
                                    backgroundColor: seg.isWeakest ? 'rgba(100, 116, 139, 0.4)' : CORES[seg.key]
                                  }}
                                />
                                
                                {/* Info */}
                                <div className="flex-1">
                                  <div className="flex items-baseline gap-2">
                                    <span className={`text-lg font-bold ${seg.isWeakest ? 'text-slate-500' : 'text-white'}`}>
                                      {seg.pct}%
                                    </span>
                                    <span className={`text-sm ${seg.isWeakest ? 'text-slate-500' : 'text-white/80'}`}>
                                      {seg.label}
                                    </span>
                                  </div>
                                  <div className="text-xs text-white/40">
                                    {seg.sublabel}
                                  </div>
                                </div>
                                
                                {/* Badge */}
                                {seg.isDominant && (
                                  <span className="text-xs text-white/70 bg-slate-600/60 px-3 py-1.5 rounded-lg border border-slate-500/30">
                                    dominante
                                  </span>
                                )}
                                {seg.isWeakest && (
                                  <span className="text-xs text-amber-400/80 flex items-center gap-1">
                                    🌙 atenção
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* ====== A TENSÃO QUE TE MOVE ====== */}
                  {(() => {
                    const dominantKey = result.dominantArchetype;
                    const isRaiz = ['peregrino', 'guardiao', 'amante', 'alquimista'].includes(dominantKey);
                    
                    // TENSAO_RAIZ é string direta, tensaoQueTeMove é objeto com .texto
                    const tensaoTexto = isRaiz 
                      ? TENSAO_RAIZ[dominantKey] 
                      : tensaoQueTeMove[dominantKey]?.texto;
                    
                    if (!tensaoTexto) return null;
                    return (
                      <div className="bg-gradient-to-br from-rose-900/20 via-slate-900/30 to-indigo-900/20 backdrop-blur-sm rounded-2xl p-6 border border-rose-500/15 mt-6" data-testid="tensao-que-te-move">
                        <div className="text-center mb-4">
                          <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">
                            A Tensão que te Move
                          </h3>
                          <p className="text-white/50 text-sm">O conflito central do seu coração</p>
                        </div>
                        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                          <p className="text-white/90 text-lg leading-relaxed italic text-center">
                            "{tensaoTexto}"
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* CAMADA 3: Clareza - Entendendo Seu Jeito de Ser */}
                  <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 backdrop-blur-sm rounded-2xl p-6 border border-emerald-500/20">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-2xl">✨</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        Entendendo Seu Jeito de Ser
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h4 className="text-base font-semibold text-emerald-200 mb-2">
                          Como você sente o mundo
                        </h4>
                        <p className="text-white/90 leading-relaxed text-sm">
                          {accessible.camada_clareza.como_voce_sente}
                        </p>
                      </div>

                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h4 className="text-base font-semibold text-emerald-200 mb-2">
                          Como você age
                        </h4>
                        <p className="text-white/90 leading-relaxed text-sm">
                          {accessible.camada_clareza.como_voce_age}
                        </p>
                      </div>

                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h4 className="text-base font-semibold text-emerald-200 mb-2">
                          O que está crescendo em você
                        </h4>
                        <p className="text-white/90 leading-relaxed text-sm">
                          {accessible.camada_clareza.o_que_esta_crescendo}
                        </p>
                      </div>

                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h4 className="text-base font-semibold text-emerald-200 mb-2">
                          O que te equilibra
                        </h4>
                        <p className="text-white/90 leading-relaxed text-sm">
                          {accessible.camada_clareza.o_que_te_equilibra}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CAMADA 4: Evolutiva - Sua Jornada de Crescimento */}
                  <div className="bg-gradient-to-br from-amber-900/30 to-yellow-900/30 backdrop-blur-sm rounded-2xl p-6 border border-amber-500/20">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-2xl">🌱</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        Sua Jornada de Crescimento
                      </h3>
                    </div>

                    <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-4">
                      <p className="text-white/90 leading-relaxed text-lg">
                        {accessible.camada_evolutiva.mensagem}
                      </p>
                      
                      <div className="border-t border-white/10 pt-4">
                        <h4 className="text-amber-200 font-semibold mb-2">
                          Uma sugestão gentil para você:
                        </h4>
                        <p className="text-white/90 leading-relaxed">
                          {accessible.camada_evolutiva.sugestao_pratica}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Conteúdo original do arquétipo - Pontos Fortes e Áreas de Atenção */}
                  <div className="bg-green-900/30 backdrop-blur-sm rounded-xl p-5 border border-green-500/30">
                    <h3 className="text-green-200 font-semibold mb-2 flex items-center gap-2">
                      <span>💪</span> Seus Pontos Fortes
                    </h3>
                    <p className="text-white/90 leading-relaxed">{archetype.prosAcolhedor}</p>
                  </div>

                  <div className="bg-amber-900/30 backdrop-blur-sm rounded-xl p-5 border border-amber-500/30">
                    <h3 className="text-2xl md:text-3xl font-extrabold text-amber-200 mb-3 flex items-center gap-2">
                      <span>⚡</span> A Sombra
                    </h3>
                    <p className="text-white/90 leading-relaxed">{archetype.contrasAcolhedor}</p>
                    {/* Defesa Característica */}
                    {defesaCaracteristica[result.dominantArchetype] && (
                      <div className="mt-4 pt-4 border-t border-amber-500/20">
                        <p className="text-amber-200/70 text-xs font-semibold uppercase tracking-wider mb-2">O que você evita sentir</p>
                        <p className="text-white/80 leading-relaxed italic">
                          {defesaCaracteristica[result.dominantArchetype]}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="bg-purple-900/30 backdrop-blur-sm rounded-xl p-5 border border-purple-500/30">
                    <h3 className="text-2xl md:text-3xl font-extrabold text-purple-200 mb-3 flex items-center gap-2">
                      <span>💡</span> Reflexão
                    </h3>
                    <p className="text-white/90 leading-relaxed italic text-lg">
                      "{perguntaClinica[result.dominantArchetype] || archetype.conselhoAcolhedor}"
                    </p>
                  </div>

                  {archetype.praticasAcolhedoras && (
                    <div className="bg-cyan-900/30 backdrop-blur-sm rounded-xl p-5 border border-cyan-500/30">
                      <h3 className="text-cyan-200 font-semibold mb-2 flex items-center gap-2">
                        <span>🌿</span> Práticas de Apoio
                      </h3>
                      <ul className="space-y-1">
                        {archetype.praticasAcolhedoras.map((pratica, i) => (
                          <li key={i} className="text-white/90 flex items-start gap-2 text-sm">
                            <span className="text-cyan-400">•</span>
                            {pratica}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

          {/* ====== DIVISOR VISUAL ENTRE AS SEÇÕES ====== */}
          <div className="relative my-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/20"></div>
            </div>
            <div className="relative flex justify-center">
              <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-900 px-8 py-4 rounded-full border border-white/20">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">📊</span>
                  <div className="text-center">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-wide">Visão Analítica</h2>
                    <p className="text-white/50 text-xs mt-0.5">Perspectiva Neuropsicológica</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ====== VERSÃO ANALÍTICA ====== */}
            {/* VERSÃO ANALÍTICA COMPLETA */}
            <div className="space-y-6">
              {/* Análise Integrada do Coração */}
              <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-6 border border-indigo-500/20">
                <div className="text-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl">📜</span>
                  </div>
                    <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">Análise Integrada do Coração</h3>
                  <p className="text-white/70 text-sm">Uma interpretação abrangente que integra todas as dimensões detectadas em seu perfil emocional.</p>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white/90 leading-relaxed text-sm" 
                     dangerouslySetInnerHTML={{ __html: generateTextualInterpretation(result).replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} 
                  />
                </div>
              </div>

              {/* Pontos Fortes */}
              {(() => {
                const practical = generatePracticalInterpretation(result);
                return (
                  <>
                    <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 backdrop-blur-sm rounded-2xl p-6 border border-green-500/20">
                      <div className="text-center mb-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-3">
                          <span className="text-xl">💪</span>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Seus Pontos Fortes em Evidência</h3>
                        <p className="text-white/70 text-sm">Estas são as qualidades que se destacam em você neste momento da sua vida.</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                        <ul className="space-y-2">
                          {practical.mainStrengths.map((strength, index) => (
                            <li key={index} className="flex items-start space-x-3 text-white/90 text-sm">
                              <span className="text-green-400 font-bold flex-shrink-0">✓</span>
                              <span>{strength}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Áreas de Atenção */}
                    <div className="bg-gradient-to-r from-amber-900/40 to-orange-900/40 backdrop-blur-sm rounded-2xl p-6 border border-amber-500/20">
                      <div className="text-center mb-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-3">
                          <span className="text-xl">⚠️</span>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Áreas que Pedem Atenção</h3>
                        <p className="text-white/70 text-sm">Aspectos que podem estar criando dificuldades ou limitando seu potencial.</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                        <ul className="space-y-2">
                          {practical.mainChallenges.map((challenge, index) => (
                            <li key={index} className="flex items-start space-x-3 text-white/90 text-sm">
                              <span className="text-amber-400 font-bold flex-shrink-0">⚡</span>
                              <span>{challenge}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Áreas para Desenvolver */}
                    <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 backdrop-blur-sm rounded-2xl p-6 border border-blue-500/20">
                      <div className="text-center mb-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center mx-auto mb-3">
                          <span className="text-xl">🌱</span>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Áreas para Desenvolver</h3>
                        <p className="text-white/70 text-sm">Caminhos práticos de crescimento que podem trazer mais equilíbrio para sua vida.</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                        <ul className="space-y-2">
                          {practical.developmentAreas.map((area, index) => (
                            <li key={index} className="flex items-start space-x-3 text-white/90 text-sm">
                              <span className="text-blue-400 font-bold flex-shrink-0">→</span>
                              <span>{area}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Orientação Personalizada */}
                    <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
                      <div className="text-center mb-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-3">
                          <span className="text-xl">💡</span>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Orientação Personalizada</h3>
                      </div>
                      <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                        <p className="text-white/90 leading-relaxed text-sm italic">"{practical.advice}"</p>
                      </div>
                    </div>

                    {/* Mapa Emocional */}
                    <div className="bg-gradient-to-r from-slate-900/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-500/20">
                      <div className="text-center mb-4">
                        <h3 className="text-xl font-bold text-white mb-2">Seu Mapa Emocional Atual</h3>
                        <p className="text-white/70 text-sm">Estas são as intensidades de cada área emocional em sua vida neste momento.</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { key: 'peregrino', label: 'Movimento & Renovação', icon: '🕰️' },
                          { key: 'amante', label: 'Conexão & Afeto', icon: '💞' },
                          { key: 'alquimista', label: 'Transformação & Sabedoria', icon: '🔥' },
                          { key: 'guardiao', label: 'Estabilidade & Proteção', icon: '🪶' }
                        ].map(({ key, label, icon }) => {
                          const score = result.mainArchetypes[key];
                          const percentage = (score / 40) * 100;
                          return (
                            <div key={key} className={`text-center p-3 rounded-xl border-2 ${
                              result.dominantArchetype === key ? 'border-blue-400 bg-blue-400/20' : 'border-white/20 bg-white/5'
                            }`}>
                              <div className="text-2xl mb-2">{icon}</div>
                              <h5 className="text-white font-medium text-xs mb-2">{label}</h5>
                              <div className="w-full bg-white/20 rounded-full h-2 mb-1">
                                <div className="bg-gradient-to-r from-blue-400 to-purple-400 h-2 rounded-full" style={{ width: `${percentage}%` }} />
                              </div>
                              <p className="text-white/60 text-xs">{Math.round(percentage)}%</p>
                            </div>
                          );
                        })}
                      </div>
                      {result.isCoracaoInteiro && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-xl border border-pink-400/30">
                          <div className="text-center">
                            <span className="text-2xl mb-2 block">🌈</span>
                            <h4 className="text-white font-bold text-lg mb-1">Estado Especial: Equilíbrio Integral</h4>
                            <p className="text-white/80 text-sm">Suas áreas emocionais estão em harmonia, indicando uma integração madura entre diferentes aspectos da sua personalidade.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Capacidades Complementares */}
                    {(practical.activeDualsExplanation.length > 0 || practical.activeTriplesExplanation.length > 0) && (
                      <div className="space-y-4">
                        <div className="text-center mb-4">
                          <h3 className="text-xl font-bold text-white mb-2">Capacidades Complementares Ativas</h3>
                          <p className="text-white/70 text-sm">Além do seu perfil principal, você também desenvolveu estas habilidades especiais.</p>
                        </div>

                        {practical.activeDualsExplanation.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="text-lg font-bold text-cyan-200 flex items-center justify-center gap-2">
                              <span>🌸</span> Habilidades de Transição
                            </h4>
                            {practical.activeDualsExplanation.map((dual) => (
                              <div key={dual.key} className="bg-cyan-900/30 border border-cyan-400/30 rounded-xl p-4">
                                <h5 className="text-cyan-200 font-bold mb-2 capitalize">{dual.key.replace('_', ' ')}</h5>
                                <p className="text-white/80 text-sm mb-2"><strong>O que significa:</strong> {dual.meaning}</p>
                                <p className="text-white/80 text-sm mb-2"><strong>Por que aparece:</strong> {dual.whenItAppears}</p>
                                <div>
                                  <strong className="text-cyan-300 text-sm">Como isso se manifesta:</strong>
                                  <ul className="mt-1 space-y-1">
                                    {dual.practicalImpact.map((impact, idx) => (
                                      <li key={idx} className="text-white/80 text-sm flex items-start space-x-2">
                                        <span className="text-cyan-400">•</span>
                                        <span>{impact}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {practical.activeTriplesExplanation.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="text-lg font-bold text-blue-200 flex items-center justify-center gap-2">
                              <span>⭐</span> Capacidades Avançadas
                            </h4>
                            {practical.activeTriplesExplanation.map((triple) => (
                              <div key={triple.key} className="bg-blue-900/30 border border-blue-400/30 rounded-xl p-4">
                                <h5 className="text-blue-200 font-bold mb-2 capitalize">{triple.key.replace('_', ' ')}</h5>
                                <p className="text-white/80 text-sm mb-2"><strong>O que significa:</strong> {triple.meaning}</p>
                                <p className="text-white/80 text-sm mb-2"><strong>Por que aparece:</strong> {triple.whenItAppears}</p>
                                <div>
                                  <strong className="text-blue-300 text-sm">Como isso se manifesta:</strong>
                                  <ul className="mt-1 space-y-1">
                                    {triple.practicalImpact.map((impact, idx) => (
                                      <li key={idx} className="text-white/80 text-sm flex items-start space-x-2">
                                        <span className="text-blue-400">•</span>
                                        <span>{impact}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

          {/* ====== COMPONENTE 1: MAPA DAS ESTAÇÕES ====== */}
          <div ref={bussolaRef} className="rounded-2xl p-4" style={{ background: '#1e1b4b' }}>
            <MapaEstacoes result={result} />
          </div>

          {/* Botões Compartilhar Bússola */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-6 mb-4">
            <button
              onClick={handleDownloadBussola}
              disabled={sharingBussola}
              data-testid="download-bussola-btn"
              className="flex items-center gap-2.5 text-white border-none rounded-full px-8 py-4 text-base font-bold transition-all duration-300 tracking-wide hover:scale-105"
              style={{
                background: sharingBussola
                  ? 'rgba(245,158,11,0.4)'
                  : 'linear-gradient(135deg, #F59E0B, #EC4899)',
                boxShadow: '0 4px 24px rgba(245,158,11,0.3)',
                cursor: sharingBussola ? 'not-allowed' : 'pointer',
              }}
            >
              {sharingBussola ? (
                <>Gerando imagem...</>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Baixar minha Bússola
                </>
              )}
            </button>
            <button
              onClick={handleShareLink}
              data-testid="share-link-btn"
              className="flex items-center gap-2.5 text-white/90 border border-white/20 rounded-full px-8 py-4 text-base font-bold transition-all duration-300 tracking-wide hover:bg-white/10 hover:scale-105"
              style={{ cursor: 'pointer' }}
            >
              <Share2 className="w-5 h-5" />
              Compartilhar link
            </button>
          </div>

          {/* ====== COMPONENTE 1.5: DINÂMICA DAS ESTAÇÕES ====== */}
          <DinamicaEstacoes result={result} />

          {/* ====== COMPONENTE 2: PARA ONDE O CORAÇÃO APONTA ====== */}
          {(() => {
            const recommendations = getDeepRecommendations(result);
            if (!recommendations || recommendations.length === 0) return null;
            
            return (
              <div className="mt-8 mb-8" data-testid="recomendacoes-profundas">
                {/* Título */}
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-r from-amber-900/40 via-orange-900/40 to-amber-900/40 px-6 py-3 rounded-full border border-amber-500/20">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-6 h-6 text-amber-400" />
                      <div className="text-center">
                        <h3 className="text-2xl md:text-3xl font-extrabold text-white">Para Onde o Coração Aponta</h3>
                        <p className="text-white/60 text-xs">O que o livro tem para despertar em você</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Grupos de recomendação */}
                <div className="space-y-6">
                  {recommendations.map((group, gi) => (
                    <div key={gi} className="rounded-2xl overflow-hidden border border-amber-500/15 bg-gradient-to-br from-amber-950/20 via-slate-900/30 to-orange-950/20" data-testid={`rec-group-${group.eixo}`}>
                      {/* Header do grupo: direção + motivo */}
                      <div className="px-5 pt-5 pb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-2 h-2 rounded-full ${
                            group.fonte === 'tensao' ? 'bg-amber-400' :
                            group.fonte === 'tripla' ? 'bg-pink-400' :
                            group.fonte === 'equilibrio' ? 'bg-emerald-400' :
                            'bg-slate-400'
                          }`} />
                          <span className="text-amber-300 text-sm font-semibold">{group.direcao}</span>
                          {group.intensidade && (
                            <span className="text-amber-400/40 text-xs">({group.intensidade})</span>
                          )}
                        </div>
                        <p className="text-white/60 text-sm leading-relaxed">
                          {group.motivo || group.convite}
                        </p>
                      </div>

                      {/* Capítulos do grupo — formato enriquecido */}
                      <div className="px-5 pb-5 space-y-4">
                        {group.chapters.map((chapter, ci) => {
                          const enriched = getChapterContent(chapter.capitulo);
                          return (
                            <div key={ci} className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
                              <h4 className="text-white font-bold text-base" data-testid={`chapter-title-${gi}-${ci}`}>
                                {chapter.capitulo}
                              </h4>
                              {enriched ? (
                                <>
                                  <p className="text-amber-300/80 text-xs font-medium">{enriched.parte} {enriched.dominio && `— ${enriched.dominio}`}</p>
                                  <p className="text-white/80 text-sm italic leading-relaxed border-l-2 border-amber-500/30 pl-4">
                                    "{enriched.abertura}"
                                  </p>
                                  <p className="text-white/70 text-sm leading-relaxed">
                                    {enriched.promessa}
                                  </p>
                                  <a
                                    href={enriched.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 rounded-lg transition-all text-sm border border-amber-500/20"
                                    data-testid={`chapter-link-${gi}-${ci}`}
                                  >
                                    <BookOpen className="w-4 h-4" />
                                    Ler este capítulo
                                    <ArrowRight className="w-3 h-3" />
                                  </a>
                                </>
                              ) : (
                                <>
                                  <p className="text-amber-300/70 text-xs">{chapter.parte} {chapter.bloco && `— ${chapter.bloco}`}</p>
                                  <p className="text-white/60 text-sm italic leading-relaxed">"{chapter.razao}"</p>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA para o livro */}
                <div className="mt-6 text-center">
                  <a
                    href="https://ocoracaodetodasascoisas.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all"
                  >
                    <BookOpen className="w-5 h-5" />
                    Conhecer o Livro
                  </a>
                </div>
              </div>
            );
          })()}

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <button
              onClick={handleDownloadPDF}
              style={{ border: 'none' }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg transition-all text-sm"
            >
              <Download className="w-4 h-4" />
              Baixar PDF
            </button>
            <button
              onClick={handleShare}
              style={{ border: 'none' }}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all text-sm"
            >
              <Share2 className="w-4 h-4" />
              Compartilhar
            </button>
            {(collectEmail || saveFormData.email) && (
              <button
                onClick={() => setShowEvolucao(true)}
                style={{ border: 'none' }}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-lg transition-all text-sm"
                data-testid="btn-evolucao"
              >
                <History className="w-4 h-4" />
                Veja sua evolução
              </button>
            )}
            {(collectEmail || saveFormData.email) && (
              <button
                onClick={() => setShowJornada(true)}
                style={{ border: 'none' }}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg transition-all text-sm"
                data-testid="btn-jornada-tempo"
              >
                🕰️ Veja sua jornada no tempo
              </button>
            )}
            <button
              onClick={() => {
                setPhase('intro');
                setCurrentQuestion(0);
                setAnswers({});
                setResult(null);
                setSaved(false);
                setSaveFormData({ nome_identificador: '', email: '', lgpd_consent: false });
              }}
              style={{ border: 'none' }}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all text-sm"
            >
              Refazer teste
            </button>
          </div>

          {/* Modal de Evolução */}
          {showEvolucao && (
            <EvolucaoEstacoes
              currentResult={result}
              email={collectEmail || saveFormData.email}
              onClose={() => setShowEvolucao(false)}
            />
          )}

          {/* Modal Jornada no Tempo */}
          {showJornada && (
            <JornadaNoTempo 
              email={collectEmail || saveFormData.email} 
              nome={collectName || saveFormData.nome_identificador} 
              onClose={() => setShowJornada(false)} 
            />
          )}

          {/* Footer */}
          <div className="text-center mt-8 text-white/40">
            <p className="text-xs">
              Todos os direitos reservados — <strong>Autor Magan</strong>
            </p>
            <p className="text-xs italic mt-1">
              "O Coração de Todas as Coisas"
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default HeartSeasonTest;
